/**
 * Sync-Audio – Aufnahmen über den Sync-Server teilen, damit Google jeden Text
 * nur einmal berechnet. Reihenfolge beim Abspielen (playbackController):
 * Gerätecache → Sync-Server → Google.
 *
 * Alles hier ist „best effort“: Ist der Server nicht erreichbar, spielt der
 * Reader wie ohne Sync, und der nächste Abgleich holt Uploads nach.
 */

import * as audioCache from '../audioCache.js';
import { getConnection } from './syncConnection.js';
import { encrypt, decrypt, audioId } from './syncCrypto.js';

/**
 * Cache-Keys, die laut letzter Vorhanden-Abfrage auf dem Server fehlen. Für
 * sie spart sich „Vorladen“ den einzelnen Abruf.
 * @type {Set<string>}
 */
let knownMissing = new Set();

/**
 * Holt eine Aufnahme vom Server
 * @param {string} cacheKey
 * @param {string} mimeType
 * @param {AbortSignal} [signal]
 * @returns {Promise<Blob|null>} null, wenn nicht gekoppelt, nicht vorhanden oder nicht erreichbar
 */
export async function download(cacheKey, mimeType, signal) {
  const connection = getConnection();
  if (!connection || knownMissing.has(cacheKey)) return null;
  try {
    const { client, keys } = connection;
    const id = await audioId(keys.idKey, cacheKey);
    const data = await client.getAudio(keys.spaceId, id, signal);
    if (!data) return null;
    return new Blob([await decrypt(keys.contentKey, data)], { type: mimeType });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    console.warn('Aufnahme nicht vom Sync-Server geladen:', error);
    return null;
  }
}

/**
 * Lädt eine neu erzeugte Aufnahme im Hintergrund hoch
 * @param {string} cacheKey
 * @param {Blob} blob
 */
export function upload(cacheKey, blob) {
  const connection = getConnection();
  if (!connection) return;
  knownMissing.delete(cacheKey);
  uploadOne(connection, cacheKey, blob).catch((error) => {
    // Kein Grund zur Sorge: uploadMissing beim nächsten Abgleich holt es nach
    console.warn('Aufnahme nicht hochgeladen:', error);
  });
}

/**
 * Fragt mit einer Anfrage ab, welche der Aufnahmen der Server hat
 * @param {string[]} cacheKeys
 */
export async function checkAvailability(cacheKeys) {
  knownMissing = new Set();
  const connection = getConnection();
  if (!connection || cacheKeys.length === 0) return;
  try {
    const ids = await serverIds(connection, cacheKeys);
    const present = await connection.client.existingAudio(connection.keys.spaceId, [...ids.keys()]);
    for (const [id, cacheKey] of ids) {
      if (!present.has(id)) knownMissing.add(cacheKey);
    }
  } catch (error) {
    console.warn('Vorhanden-Abfrage beim Sync-Server gescheitert:', error);
  }
}

/**
 * Vergisst das Ergebnis der letzten Vorhanden-Abfrage. Danach hochgeladene
 * Aufnahmen anderer Geräte werden wieder abgefragt statt neu berechnet.
 */
export function forgetAvailability() {
  knownMissing = new Set();
}

/**
 * Lädt alle Aufnahmen dieses Geräts hoch, die dem Server fehlen – nach dem
 * Koppeln oder wenn ein Upload unterwegs gescheitert ist
 * @returns {Promise<number>} Anzahl hochgeladener Aufnahmen
 */
export async function uploadMissing() {
  const connection = getConnection();
  if (!connection) return 0;
  // Der Gerätecache fasst höchstens 500 Aufnahmen – das passt in eine Abfrage.
  const ids = await serverIds(connection, await audioCache.keys());
  if (ids.size === 0) return 0;
  const present = await connection.client.existingAudio(connection.keys.spaceId, [...ids.keys()]);

  let uploaded = 0;
  for (const [id, cacheKey] of ids) {
    if (present.has(id)) continue;
    const blob = await audioCache.blobForKey(cacheKey);
    if (!blob) continue;
    await uploadOne(connection, cacheKey, blob);
    uploaded++;
  }
  return uploaded;
}

/**
 * @param {import('./syncConnection.js').Connection} connection
 * @param {string} cacheKey
 * @param {Blob} blob
 */
async function uploadOne({ client, keys }, cacheKey, blob) {
  const data = await encrypt(keys.contentKey, new Uint8Array(await blob.arrayBuffer()));
  await client.putAudio(keys.spaceId, await audioId(keys.idKey, cacheKey), data);
}

/**
 * Bildet die Server-Kennungen zu Cache-Keys
 * @param {import('./syncConnection.js').Connection} connection
 * @param {string[]} cacheKeys
 * @returns {Promise<Map<string, string>>} Server-Kennung → Cache-Key
 */
async function serverIds({ keys }, cacheKeys) {
  const ids = await Promise.all(cacheKeys.map((key) => audioId(keys.idKey, key)));
  return new Map(ids.map((id, i) => [id, cacheKeys[i]]));
}
